import { Box, Flex, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { fontSize } from 'styles/revampTokens';

import colors from '../dataTheme';

import DimensionType from './ActionTwoSteps/DimensionType';
import DataType from './ActionTwoSteps/DataType';
import { RadioDot, StepHeading, optionCardStyle } from './actionStepUi';

interface Properties {
  setHelpModalSeleceted: any;
  flags?: any;
}

/** Returns the step-2 panel heading based on the selected import type. */
function stepTwoHeading(dataType: string | null): string {
  if (dataType === '1') return 'Set up your dimension import';
  if (dataType === '2') return 'Set up your actuals import';
  if (dataType === '3') return 'Set up your forecast import';
  return 'Where do you want to load this data?';
}

/**
 * Step 2 of the import wizard — lets the user pick the import type (dimension / actuals / forecast)
 * then renders the matching sub-step form.
 *
 * Note: Several JSX lines carry `@ts-ignore` because Chakra UI builds very deep prop union types.
 * When composed with `withLDConsumer` TypeScript exceeds its union-instantiation limit (TS2589).
 * The ignores are intentionally narrow and do not mask real type errors.
 */
const ActionStepTwo = ({ setHelpModalSeleceted, flags }: Properties) => {
  /** Narrow selector avoids inferring the whole form object, reducing TS union depth. */
  const actionFormDataType = useSelector(
    (state: any) => state.dataImportSlice.actionFormData.dataType as string | undefined,
  );

  const [dataType, setDataType] = useState<string | null>(() => {
    const value = actionFormDataType;
    return value === '' || value === undefined || value === null ? null : value;
  });

  // Edit flow: step may mount before getOneActionData fills Redux — keep radio in sync.
  useEffect(() => {
    const value = actionFormDataType;
    if (value === '1' || value === '2' || value === '3') {
      setDataType(value);
    } else if (value === '' || value === undefined || value === null) {
      setDataType(null);
    }
  }, [actionFormDataType]);

  const options = [
    {
      value: '1',
      label: 'Load dimension items',
      hint: 'Add, update or remove items in a dimension — e.g. employees, cost centres.',
      enabled: true,
    },
    {
      value: '2',
      label: 'Load actual data',
      hint: 'Import actual values from an external system into your model.',
      enabled: Boolean(flags?.importAsDataType),
    },
    {
      value: '3',
      label: 'Load forecast data',
      hint: 'Import forecast values into a scenario. Only loads data to the forecast, not actuals.',
      enabled: Boolean(flags?.importAsForecastType),
    },
  ].filter((option) => option.enabled);

  return (
    <Box width='100%'>
      <StepHeading
        title={stepTwoHeading(dataType)}
        subtitle='Choose what this action should import.'
      />
      <Flex direction='column' gap={2}>
        {options.map((option) => {
          const isSelected = dataType === option.value;
          return (
            <Flex
              key={option.value}
              as='button'
              type='button'
              role='radio'
              aria-checked={isSelected}
              {...optionCardStyle(isSelected)}
              onClick={() => setDataType(option.value)}
            >
              <RadioDot isSelected={isSelected} />
              <Box minW={0}>
                <Text {...PLAN_TEXT.bodyMedium} fontSize={fontSize.body} color={colors.textDark}>
                  {option.label}
                </Text>
                <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt='2px'>
                  {option.hint}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Flex>

      {dataType === '1' ? (
        <DimensionType setHelpModalSeleceted={setHelpModalSeleceted} dataType={dataType} />
      ) : dataType === '2' || dataType === '3' ? (
        <DataType setHelpModalSeleceted={setHelpModalSeleceted} dataType={dataType} />
      ) : null}
    </Box>
  );
};

export default withLDConsumer()(ActionStepTwo);
